import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { useAuth } from "@/auth/AuthContext";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function Register() {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    document.title = "Create account · RigBooks";
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim() || password.length < 6) {
      return toast.error("Enter a valid email and a 6+ character password.");
    }
    setBusy(true);
    try {
      await register(email.trim(), password, name.trim() || undefined);
      navigate("/dashboard", { replace: true });
    } catch (err) {
      toast.error(err?.message || "Registration failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-rig-bg px-5 py-10">
      <div className="w-full max-w-md">
        <div className="mb-8 flex flex-col items-center gap-4 text-center">
          <Logo size={44} />
          <p className="font-display text-xs font-bold uppercase tracking-[0.2em] text-rig-dim">
            Start your audit-ready logbook
          </p>
        </div>
        <div className="rounded-3xl border border-rig-border bg-rig-surface p-7 sm:p-9">
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label htmlFor="name" className="font-display text-xs uppercase tracking-widest text-rig-dim">
                Name (optional)
              </Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="John D."
                data-testid="register-name-input"
                className="mt-1.5 h-12 border-rig-borderStrong bg-rig-bg text-white placeholder:text-rig-mute"
              />
            </div>
            <div>
              <Label htmlFor="email" className="font-display text-xs uppercase tracking-widest text-rig-dim">
                Email
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="driver@example.com"
                data-testid="register-email-input"
                className="mt-1.5 h-12 border-rig-borderStrong bg-rig-bg text-white placeholder:text-rig-mute"
                autoComplete="email"
              />
            </div>
            <div>
              <Label htmlFor="password" className="font-display text-xs uppercase tracking-widest text-rig-dim">
                Password
              </Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="At least 6 characters"
                data-testid="register-password-input"
                className="mt-1.5 h-12 border-rig-borderStrong bg-rig-bg text-white placeholder:text-rig-mute"
                autoComplete="new-password"
              />
            </div>

            <Button
              type="submit"
              disabled={busy}
              data-testid="register-submit-button"
              className="h-12 w-full bg-rig-accent font-display text-base font-bold uppercase tracking-[0.2em] text-rig-accentText hover:bg-rig-accentDark"
            >
              {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : "Create account"}
            </Button>
          </form>

          <div className="mt-6 text-center text-sm text-rig-dim">
            Already have an account?{" "}
            <Link to="/login" data-testid="go-to-login-link" className="font-semibold text-rig-accent hover:underline">
              Sign in
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
